import { z } from 'zod';

/**
 * Schemas comunes reutilizados en todo el dominio.
 * Estos son la fuente de verdad — tanto el backend (Lambda) como el
 * frontend (Vite app) importan desde aquí para tipar requests y forms.
 */

/** ULID: identificador lexicográficamente ordenable (26 chars Crockford base32). */
export const UlidSchema = z
  .string()
  .length(26)
  .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'Invalid ULID');
export type Ulid = z.infer<typeof UlidSchema>;

/** ID de tenant (condominio). Mismo formato que ULID. */
export const TenantIdSchema = UlidSchema.brand<'TenantId'>();
export type TenantId = z.infer<typeof TenantIdSchema>;

/** RUT chileno en formato canónico con puntos y guión. */
export const RutSchema = z
  .string()
  .regex(/^\d{1,3}(\.\d{3})*-[\dkK]$/, 'RUT debe tener formato XX.XXX.XXX-D');
export type Rut = z.infer<typeof RutSchema>;

/** RUT normalizado (sin separadores). */
export const RutNormalizedSchema = z
  .string()
  .regex(/^\d{7,8}[\dK]$/, 'RUT normalizado inválido');
export type RutNormalized = z.infer<typeof RutNormalizedSchema>;

/** Monto en peso chileno (siempre entero, positivo o cero). */
export const MontoClpSchema = z.number().int().nonnegative();
export type MontoClp = z.infer<typeof MontoClpSchema>;

/** Monto en UF con hasta 4 decimales. */
export const MontoUfSchema = z.number().nonnegative().multipleOf(0.0001);
export type MontoUf = z.infer<typeof MontoUfSchema>;

/**
 * Alícuota — fracción [0, 1] de participación en el condominio.
 * Se serializa como número con hasta 6 decimales.
 */
export const AlicuotaSchema = z.number().min(0).max(1);
export type Alicuota = z.infer<typeof AlicuotaSchema>;

/** Metros cuadrados (positivo, hasta 2 decimales). */
export const MetrosCuadradosSchema = z.number().positive().multipleOf(0.01);
export type MetrosCuadrados = z.infer<typeof MetrosCuadradosSchema>;

/** Email con normalización a minúsculas. */
export const EmailSchema = z.email().toLowerCase();
export type Email = z.infer<typeof EmailSchema>;

/** Teléfono chileno: +569XXXXXXXX o +562XXXXXXXX. */
export const TelefonoChileSchema = z
  .string()
  .regex(/^\+56[0-9]{9}$/, 'Teléfono debe tener formato +569XXXXXXXX');
export type TelefonoChile = z.infer<typeof TelefonoChileSchema>;

/** Timestamp ISO 8601 en UTC. */
export const IsoTimestampSchema = z.iso.datetime({ offset: true });
export type IsoTimestamp = z.infer<typeof IsoTimestampSchema>;

/** Fecha ISO (YYYY-MM-DD). */
export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export type IsoDate = z.infer<typeof IsoDateSchema>;

/** Período mensual (YYYY-MM). */
export const PeriodoSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
export type Periodo = z.infer<typeof PeriodoSchema>;

/** Paginación cursor-based. */
export const PaginationSchema = z.object({
  limit: z.number().int().positive().max(100).optional().default(20),
  cursor: z.string().optional(),
});
export type Pagination = z.infer<typeof PaginationSchema>;

/** Respuesta paginada genérica. */
export function paginatedResponse<T extends z.ZodType>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema),
    nextCursor: z.string().optional(),
  });
}

/** Rol del usuario autenticado. */
export const RolSchema = z.enum(['super_admin', 'admin', 'comite', 'conserje', 'propietario', 'residente']);
export type Rol = z.infer<typeof RolSchema>;
